import { createRoot } from "react-dom/client";
import App from "./App";
import { initializeTenant, getTenantError } from "./config/tenant";
import { applyBranding } from "./styles/branding";
import TenantErrorPage from "./pages/TenantErrorPage";
import { RootErrorBoundary } from "./RootErrorBoundary";

const root = createRoot(document.getElementById("root")!);

const publicUrl = (process.env.PUBLIC_URL ?? "").replace(/\/$/, "");

/** Avoid infinite white screen if chain-info never completes (proxy/network hang). */
const TENANT_BOOT_TIMEOUT_MS = 20_000;

function BootLoading() {
	return (
		<div
			style={{
				minHeight: "100dvh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				fontFamily: "system-ui, sans-serif",
				background: "#FAF9F6",
				color: "#1a1a1a",
			}}
		>
			Loading…
		</div>
	);
}

async function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
	let timeoutId: ReturnType<typeof setTimeout>;
	const timeout = new Promise<never>((_, reject) => {
		timeoutId = setTimeout(() => reject(new Error(`${label} (${ms} ms)`)), ms);
	});
	try {
		return await Promise.race([promise, timeout]);
	} finally {
		clearTimeout(timeoutId!);
	}
}

async function bootstrap() {
	root.render(<BootLoading />);
	try {
		const tenant = await withTimeout(
			initializeTenant(),
			TENANT_BOOT_TIMEOUT_MS,
			"Check-in service did not respond in time"
		);

		applyBranding(tenant.branding, tenant.chainName);

		const favicon = tenant.branding.faviconUrl?.trim();
		if (favicon) {
			setFaviconFromUrl(favicon);
		} else {
			setDefaultFavicon();
		}

		root.render(
			<RootErrorBoundary>
				<App />
			</RootErrorBoundary>
		);
	} catch (err) {
		console.error("Bootstrap failed:", err);
		const error = getTenantError() ?? "NETWORK_ERROR";
		root.render(<TenantErrorPage error={error} />);
	}
}

/** Generic check-in icon (matches public/favicon-default.svg) — not tenant-specific */
function setDefaultFavicon(): void {
	const href = `${publicUrl}/favicon-default.svg`;
	replaceFaviconLinks({
		href,
		type: "image/svg+xml",
		appleTouchHref: `${publicUrl}/logo192.png`,
	});
}

function setFaviconFromUrl(url: string): void {
	const lower = url.split("?")[0].toLowerCase();
	let type: string | undefined;
	if (lower.endsWith(".svg")) type = "image/svg+xml";
	else if (lower.endsWith(".png")) type = "image/png";
	else if (lower.endsWith(".gif")) type = "image/gif";
	else if (lower.endsWith(".ico")) type = "image/x-icon";
	replaceFaviconLinks({ href: url, type, appleTouchHref: url });
}

function replaceFaviconLinks(options: {
	href: string;
	type?: string;
	/** Optional; defaults to same as favicon for hotels that provide a square mark */
	appleTouchHref?: string;
}): void {
	const head = document.head;
	head.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]').forEach((el) => head.removeChild(el));
	const link = document.createElement("link");
	link.rel = "icon";
	link.href = options.href;
	if (options.type) {
		link.type = options.type;
	}
	head.appendChild(link);

	head.querySelectorAll('link[rel="apple-touch-icon"]').forEach((el) => head.removeChild(el));
	const apple = document.createElement("link");
	apple.rel = "apple-touch-icon";
	apple.href = options.appleTouchHref ?? options.href;
	head.appendChild(apple);
}

bootstrap();
